import React from 'react';
import {
  ProjectToolLogMessage,
  ProjectToolLogMessageLevel,
  ProjectToolStatus,
} from 'domain/projects/shared/projectToolsCreation';
import { Badge, SmallLoader } from 'framework/ui/mui-builder';
import Cancel from '@mui/icons-material/Cancel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Warning from '@mui/icons-material/Warning';
import {
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Step,
  StepContent,
  StepLabel,
  StepProps,
  Typography,
} from '@mui/material';

type ProjectToolsFormStepProps = {
  DefaultLabelIconComponent?: React.ElementType;
  label: React.ReactNode;
  status?: ProjectToolStatus;
  logs?: ProjectToolLogMessage[];
  form?: React.ReactNode;
} & StepProps;

const ProjectToolsFormStep = ({
  status,
  logs,
  label,
  DefaultLabelIconComponent,
  form,
  ...props
}: ProjectToolsFormStepProps) => {
  const getStepIcon = (toolStatus?: ProjectToolStatus) => {
    const defaultComponent = DefaultLabelIconComponent ? () => <DefaultLabelIconComponent /> : undefined;

    if (!toolStatus) {
      return defaultComponent;
    }

    switch (toolStatus) {
      case 'in_progress':
        return () => <SmallLoader />;
      case 'passed':
        return () => <CheckCircle sx={{ color: 'success.main' }} />;
      case 'warning':
        return () => <Warning sx={{ color: 'warning.dark' }} />;
      case 'failed':
        return () => <Cancel sx={{ color: 'error.main' }} />;
      default:
        return defaultComponent;
    }
  };

  const getMessageColor = (messageStatus: ProjectToolLogMessageLevel) => {
    switch (messageStatus) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'instruction':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Step {...props}>
      <StepLabel
        slots={{
          stepIcon: getStepIcon(status),
        }}
      >
        <Typography variant="h6" color="primary">
          {label}
        </Typography>
      </StepLabel>
      <StepContent>
        {logs && (
          <Alert icon={false} variant="outlined" severity="warning" sx={{ mb: 2 }}>
            <List dense disablePadding>
              {logs.map((v, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <ListItem disableGutters dense disablePadding key={`${label}-message-${i}`}>
                  <ListItemIcon sx={{ minWidth: '80px' }}>
                    <Badge color={getMessageColor(v.level)} sx={{ textTransform: 'capitalize' }}>
                      {v.level}
                    </Badge>
                  </ListItemIcon>
                  <ListItemText primary={v.message} />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
        {form}
      </StepContent>
    </Step>
  );
};

export default ProjectToolsFormStep;
